import {observable, action, decorate} from 'mobx';
import api from "../../utils/api";
import {DataResponse, GenericResponse} from '../models/genericResponseModels';
import {
    LicenseAuditStatsDTO,
    LicenseManagerFormDTO, 
    LicenseReviewDTO,
    LicenseReviewActionsDTO,
    LicenseReviewSubmitFormDTO,
    LicenseGrantAccessRequest,
    LicenseRevokeAccessRequest,
    LicenseFormAccessPermissionDTO
} from '../models/licenseModels';

export interface LicenseAudit {
    id: string;
    isCompleted: boolean;
    startDate: Date;
    endDate: Date;
    totalReviews: number;
    reviewsCompleted: number;
    department: string;
}

type FormType = 'own' | 'granted';

interface FormCache {
    [auditId: string]: {
        [formKey: string]: LicenseManagerFormDTO;
    };
}

class AuditDataStore {
    auditData: LicenseAudit[] = [];
    selectedAudit: LicenseAudit | undefined = undefined;
    auditStats: LicenseAuditStatsDTO | null = null;
    fetchError: string | null = null;
    loading: boolean = false;
    incompleteActions: string[] = [];
    formButtonColor: 'default' | 'success' | 'error' = 'default';
    currentFormType: FormType = 'own';
    selectedGrantedFormIndex: number = 0;
    formCache: FormCache = {};
    availableGrantedForms: Array<{email: string, name: string, index: number}> = [];

    initialFormState: LicenseManagerFormDTO = {
        id: '',
        managerName: '',
        managerEmail: '',
        comment: '',
        licenseReviews: []
    };

    currentFormState: LicenseManagerFormDTO = {
        id: '',
        managerName: '',
        managerEmail: '',
        comment: '',
        licenseReviews: []
    };

    getCurrentCacheKey(): string {
        if (!this.selectedAudit) return '';
        const formTypeKey = this.currentFormType === 'own' ? 'own' : `granted_${this.selectedGrantedFormIndex}`;
        return `${this.selectedAudit.id}_${formTypeKey}`;
    }

    hasFormChanges(): boolean {
        if (!this.currentFormState.id || !this.initialFormState.id) return false;
        
        // Compare comment
        if (this.currentFormState.comment !== this.initialFormState.comment) return true;
        
        // Compare license review actions
        if (this.currentFormState.licenseReviews.length !== this.initialFormState.licenseReviews.length) return true;
        
        for (let i = 0; i < this.currentFormState.licenseReviews.length; i++) {
            const currentReview = this.currentFormState.licenseReviews[i];
            const initialReview = this.initialFormState.licenseReviews[i];
            
            if (currentReview.licenseReviewActions.length !== initialReview.licenseReviewActions.length) return true;
            
            for (let j = 0; j < currentReview.licenseReviewActions.length; j++) {
                const currentAction = currentReview.licenseReviewActions[j];
                const initialAction = initialReview.licenseReviewActions[j];
                
                // Compare selected options arrays
                if (currentAction.selectedOptions.length !== initialAction.selectedOptions.length) return true;
                if (!currentAction.selectedOptions.every(option => initialAction.selectedOptions.includes(option))) return true;
            }
        }
        
        return false;
    }

    saveToCache(auditId?: string) {
        // Use provided auditId or fall back to current selectedAudit
        const targetAuditId = auditId || this.selectedAudit?.id;
        const targetAudit = this.auditData.find(audit => audit.id === targetAuditId);

        if (targetAudit?.isCompleted) {
            return;
        }
        
        const cacheKey = this.getCurrentCacheKey();
        if (!cacheKey || !targetAuditId) {
            return;
        }

        if (this.currentFormState.id) {
            if (!this.formCache[targetAuditId]) {
                this.formCache[targetAuditId] = {};
            }
            
            // Create independent deep copy for caching
            this.formCache[targetAuditId][cacheKey] = JSON.parse(JSON.stringify(this.currentFormState));
        }
    }

    loadFromCache(): boolean {
        const cacheKey = this.getCurrentCacheKey();
        if (!cacheKey || !this.selectedAudit) {
            return false;
        }
        
        const cachedForm = this.formCache[this.selectedAudit.id]?.[cacheKey];
        if (cachedForm) {
            // Validate cached form data compatibility with current auditStats
            if (this.auditStats && this.isFormDataCompatible(cachedForm)) {
                // Create deep copies to prevent reference sharing
                this.currentFormState = JSON.parse(JSON.stringify(cachedForm));
                this.initialFormState = JSON.parse(JSON.stringify(cachedForm));
                return true;
            } else {
                // Remove incompatible cache entry
                delete this.formCache[this.selectedAudit.id][cacheKey];
                return false;
            }
        }
        return false;
    }

    private isFormDataCompatible(formData: LicenseManagerFormDTO): boolean {
        if (!this.auditStats || !this.auditStats.licenseAuditActions) {
            return false;
        }

        // Check if all action IDs in the form exist in current auditStats
        for (const review of formData.licenseReviews) {
            for (const action of review.licenseReviewActions) {
                const actionExists = this.auditStats.licenseAuditActions.some(auditAction => 
                    auditAction.id === action.actionId
                );
                if (!actionExists) {
                    return false;
                }

                // Check if all selected option IDs exist in current auditStats
                for (const optionId of action.selectedOptions) {
                    if (!optionId) {
                        continue;
                    }
                    
                    const optionExists = this.auditStats.licenseAuditActions.some(auditAction =>
                        auditAction.licenseActionOptions.some(option => option.id === optionId)
                    );
                    if (!optionExists) {
                        return false;
                    }
                }
            }
        }

        return true;
    }

    setFormType(type: FormType, grantedFormIndex?: number) {
        // Save current form to cache before switching (only if not completed)
        if (!this.selectedAudit?.isCompleted && this.selectedAudit?.id && this.currentFormState.id) {
            this.saveToCache(this.selectedAudit.id);
        }
        
        this.currentFormType = type;
        if (type === 'granted' && grantedFormIndex !== undefined) {
            this.selectedGrantedFormIndex = grantedFormIndex;
        }
        
        // Try cache first, fallback to auditStats
        if (!this.loadFromCache()) {
            this.loadFormFromAuditStats();
        }
    }

    loadFormFromAuditStats() {
        if (!this.auditStats) return;
        
        let formToLoad: LicenseManagerFormDTO | undefined;
        
        if (this.currentFormType === 'own' && this.auditStats.licenseManagerForms?.length > 0) {
            formToLoad = this.auditStats.licenseManagerForms[0];
        } else if (this.currentFormType === 'granted' && this.auditStats.grantedLicenseManagerForms?.length > this.selectedGrantedFormIndex) {
            formToLoad = this.auditStats.grantedLicenseManagerForms[this.selectedGrantedFormIndex];
        }
        
        if (formToLoad) {
            // Create completely independent deep copies
            this.initialFormState = JSON.parse(JSON.stringify(formToLoad));
            this.currentFormState = JSON.parse(JSON.stringify(formToLoad));
        }
    }

    updateAvailableGrantedForms() {
        this.availableGrantedForms = [];
        if (this.auditStats?.grantedLicenseManagerForms) {
            this.availableGrantedForms = this.auditStats.grantedLicenseManagerForms.map((form, index) => ({
                email: form.managerEmail,
                name: form.managerName,
                index
            }));
        }
    }

    setReview(data: LicenseAudit) {
        this.selectedAudit = data;
    }

    setAuditStats(data: LicenseAuditStatsDTO) {
        this.auditStats = JSON.parse(JSON.stringify(data));
        this.updateAvailableGrantedForms();

        if (this.currentFormType === 'own' && !this.currentFormState.id) {
            this.loadFormFromAuditStats();
        }
    }

    setFetchError(error: string | null) {
        this.fetchError = error;
    }

    setLoading(value: boolean) {
        this.loading = value;
    }

    setIncompleteActions(actions: string[]) {
        this.incompleteActions = actions;
    }

    cloneInitialFormState() {
        if (!this.loadFromCache()) {
            this.loadFormFromAuditStats();
        }
    }

    updateSelectedOptions(actionId: string, selectedOptions: string[]) {
        if (this.selectedAudit?.isCompleted) {
            return;
        }
        
        this.currentFormState?.licenseReviews.forEach(review => {
            review.licenseReviewActions.forEach(action => {
                if (action.id === actionId) {
                    action.selectedOptions = [...selectedOptions];
                }
            });
        });
        this.formButtonColor = 'default';
        if (this.selectedAudit?.id) {
            this.saveToCache(this.selectedAudit.id);
        }
    }

    updateComment(newComment: string) {
        if (this.selectedAudit?.isCompleted) {
            return;
        }
        
        this.currentFormState.comment = newComment;
        this.formButtonColor = 'default';
        if (this.selectedAudit?.id) {
            this.saveToCache(this.selectedAudit.id);
        }
    }

    getOptionNamesByIds(optionIds: string[]): string[] {
        if (!this.auditStats || !this.auditStats.licenseAuditActions) {
            return optionIds;
        }

        return optionIds.map(optionId => {
            for (const action of this.auditStats!.licenseAuditActions) {
                const option = action.licenseActionOptions.find(opt => opt.id === optionId);
                if (option) {
                    return option.option;
                }
            }
            return optionId;
        });
    }

    async fetchAuditData(forManager?: boolean) {
        this.setLoading(true);
        try {
            const url = forManager !== undefined
                ? `/License/GetLicenseAudits?forManager=${forManager}`
                : `/License/GetLicenseAudits`;
            const response = await api.get<DataResponse<LicenseAudit[]>>(url);
            if (response.data.isSuccessful) {
                if (Array.isArray(response.data.data) && response.data.data.length > 0) {
                    this.auditData = response.data.data;
                    this.sortAuditData();
                    this.setReview(this.auditData[this.auditData.length - 1]);
                    this.setFetchError(null);
                } else {
                    this.setFetchError('No audit data found.');
                }
            } else {
                this.setFetchError('Failed to fetch review data.');
            }
        } catch (error) {
            console.error('Failed to fetch review data:', error);
            this.setFetchError('An error occurred while fetching review data.');
        } finally {
            this.setLoading(false);
        }
    }

    async fetchPreviousOptions() {
        if (!this.selectedAudit || this.selectedAudit.isCompleted) {
            return;
        }

        this.setLoading(true);
        
        try {
            const response = await api.get<DataResponse<LicenseReviewActionsDTO[]>>(`/License/GetPreviousOptions?auditId=${this.selectedAudit.id}`);

            if (response.data.isSuccessful && response.data.data) {
                const previousOptions = response.data.data;
                
                // Create deep copy to avoid mutating other cached forms
                const updatedFormState: LicenseManagerFormDTO = JSON.parse(JSON.stringify(this.currentFormState));
                
                // Apply previous options to the current form
                updatedFormState.licenseReviews.forEach((review: LicenseReviewDTO) => {
                    review.licenseReviewActions.forEach((action: LicenseReviewActionsDTO) => {
                        const previousAction = previousOptions.find(opt => opt.id === action.id);
                        if (previousAction) {
                            action.selectedOptions = [...previousAction.selectedOptions];
                        }
                    });
                });
                
                // Update both states to make populated form the new baseline
                this.currentFormState = updatedFormState;
                this.initialFormState = JSON.parse(JSON.stringify(updatedFormState));
                
                // Cache the populated form
                if (this.selectedAudit?.id) {
                    this.saveToCache(this.selectedAudit.id);
                }
            } else {
                console.error('Failed to fetch previous options:', response.data.errorMessages?.join(', '));
                this.setFetchError('Failed to load previous options');
            }
        } catch (error) {
            console.error('Error fetching previous options:', error);
            this.setFetchError('Failed to load previous options');
        } finally {
            this.setLoading(false);
        }
    }

    sortAuditData() {
        this.auditData = [...this.auditData].sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
    }

    async handleTabChange(newValue: number, includeForms?: boolean) {
        const oldAuditId = this.selectedAudit?.id;
        
        // Save current form to cache before switching audits
        if (oldAuditId && this.currentFormState.id) {
            this.saveToCache(oldAuditId);
        }
        
        const selectedAudit = this.auditData[newValue];
        if (selectedAudit === undefined) {
            this.setFetchError('There are no reviews available.');
            return;
        }
        this.setReview(selectedAudit);

        try {
            const url = includeForms !== undefined
                ? `/License/GetLicenseAuditData?auditId=${selectedAudit.id}&includeForms=${includeForms}`
                : `/License/GetLicenseAuditData?auditId=${selectedAudit.id}`;
            const response = await api.get<DataResponse<LicenseAuditStatsDTO>>(url);
            if (response.data.isSuccessful) {
                this.setAuditStats(response.data.data);
                // Reset to own form when switching audits, but keep the same form type if possible
                if (this.currentFormType === 'granted' && this.availableGrantedForms.length <= this.selectedGrantedFormIndex) {
                    this.currentFormType = 'own';
                    this.selectedGrantedFormIndex = 0;
                }
                // If no granted forms are available, ensure we're on own forms
                if (this.availableGrantedForms.length === 0) {
                    this.currentFormType = 'own';
                }
                this.cloneInitialFormState();
            } else {
                this.setFetchError('Failed to fetch audit details.');
            }
        } catch (error) {
            console.error('Failed to fetch audit details:', error);
            this.setFetchError('An error occurred while fetching audit details.');
        }
    }

    async handleApplyOptions(actions: LicenseReviewActionsDTO[], title?: string, excludeEmail?: string) {
        if (this.selectedAudit?.isCompleted) {
            return;
        }
        
        this.currentFormState.licenseReviews.forEach(review => {
            if (excludeEmail && review.email === excludeEmail) {
                return;
            }
            
            if (!title || review.title === title) {
                review.licenseReviewActions.forEach(action => {
                    const matchingAction = actions.find(a => a.actionId === action.actionId);
                    if (matchingAction) {
                        const selectedOptions = Array.isArray(matchingAction.selectedOptions)
                            ? matchingAction.selectedOptions : [matchingAction.selectedOptions];
                        action.selectedOptions = [...selectedOptions];
                    }
                });
            }
        });
        if (this.selectedAudit?.id) {
            this.saveToCache(this.selectedAudit.id);
        }
    }

    async saveLicenseForm(data: LicenseReviewSubmitFormDTO) {
        const hasSelectedOptions = data.licenseReviews.length > 0;
        const hasComment = data.comment && data.comment.trim().length > 0;
        
        if (!hasSelectedOptions && !hasComment) {
            return {success: false, message: 'No data to save', severity: 'info'};
        }

        try {
            const response = await api.post<GenericResponse>('/License/SaveLicenseForm', data);

            if (!response.data.isSuccessful) {
                throw new Error(response.data.errorMessages.join(', '));
            }

            // Update original state to reflect saved changes
            this.initialFormState = JSON.parse(JSON.stringify(this.currentFormState));
            this.formButtonColor = 'success';

            // Update auditStats to reflect the saved changes
            if (this.auditStats) {
                if (this.currentFormType === 'own' && this.auditStats.licenseManagerForms?.length > 0) {
                    this.auditStats.licenseManagerForms[0] = JSON.parse(JSON.stringify(this.currentFormState));
                } else if (this.currentFormType === 'granted' && this.auditStats.grantedLicenseManagerForms?.length > this.selectedGrantedFormIndex) {
                    this.auditStats.grantedLicenseManagerForms[this.selectedGrantedFormIndex] = JSON.parse(JSON.stringify(this.currentFormState));
                }
            }

            // Clear cache for this form since it's been saved
            const cacheKey = this.getCurrentCacheKey();
            if (cacheKey && this.selectedAudit && this.formCache[this.selectedAudit.id]) {
                delete this.formCache[this.selectedAudit.id][cacheKey];
            }

            return {success: true, message: 'Data saved successfully', severity: 'success'};
        } catch (error) {
            console.error('Error saving data:', error);
            this.formButtonColor = 'error';
            return {success: false, message: 'Error saving data', severity: 'error'};
        }
    }

    async grantFormAccess(request: LicenseGrantAccessRequest) {
        try {
            const response = await api.post<GenericResponse>('/License/GrantFormAccess', request);
            
            if (!response.data.isSuccessful) {
                throw new Error(response.data.errorMessages.join(', '));
            }

            return {success: true, message: 'Access granted successfully', severity: 'success'};
        } catch (error) {
            console.error('Error granting access:', error);
            return {success: false, message: 'Error granting access', severity: 'error'};
        }
    }

    async revokeFormAccess(request: LicenseRevokeAccessRequest) {
        try {
            const response = await api.post<GenericResponse>('/License/RevokeFormAccess', request);
            
            if (!response.data.isSuccessful) {
                throw new Error(response.data.errorMessages.join(', '));
            }

            return {success: true, message: 'Access revoked successfully', severity: 'success'};
        } catch (error) {
            console.error('Error revoking access:', error);
            return {success: false, message: 'Error revoking access', severity: 'error'};
        }
    }

    async getLicenseFormAccessPermissions(licenseFormId: string) {
        try {
            const response = await api.get<DataResponse<LicenseFormAccessPermissionDTO[]>>(`/License/GetLicenseFormAccessPermissions?licenseFormId=${licenseFormId}`);
            
            if (!response.data.isSuccessful) {
                throw new Error(response.data.errorMessages.join(', '));
            }

            return {success: true, data: response.data.data || [], message: 'Permissions loaded successfully'};
        } catch (error) {
            console.error('Error fetching permissions:', error);
            return {success: false, data: [], message: 'Error fetching permissions'};
        }
    }
}

decorate(AuditDataStore, {
    auditData: observable,
    selectedAudit: observable,
    auditStats: observable,
    fetchError: observable,
    loading: observable,
    initialFormState: observable,
    currentFormState: observable,
    formButtonColor: observable,
    setReview: action,
    setAuditStats: action,
    setFetchError: action,
    setLoading: action,
    fetchAuditData: action,
    handleTabChange: action,
    sortAuditData: action,
    getOptionNamesByIds: action,
    updateSelectedOptions: action,
    updateComment: action,
    handleApplyOptions: action,
    saveLicenseForm: action,
    fetchPreviousOptions: action,
    incompleteActions: observable,
    setIncompleteActions: action,
    grantFormAccess: action,
    revokeFormAccess: action,
    getLicenseFormAccessPermissions: action,
    currentFormType: observable,
    selectedGrantedFormIndex: observable,
    formCache: observable,
    availableGrantedForms: observable,
    setFormType: action,
    loadFormFromAuditStats: action,
    updateAvailableGrantedForms: action,
    hasFormChanges: action
});

const auditDataStore = new AuditDataStore();
export default auditDataStore;